import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faInfoCircle,
  faBus,
  faHistory,
  faCheck,
  faClock,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const SHUTTLE_BOOKING_CLOSED = true;

export const Shuttle = () => {
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [selectedDepartureTime, setSelectedDepartureTime] = useState("");
  const [selectedDepartureDate, setSelectedDepartureDate] = useState("");
  const [selectedPickupPoint, setSelectedPickupPoint] = useState("");
  const [selectedReturnTime, setSelectedReturnTime] = useState("");
  const [selectedReturnDate, setSelectedReturnDate] = useState("Sunday, July 26, 2026");
  const [selectedReturnPoint, setSelectedReturnPoint] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [shuttleOrders, setShuttleOrders] = useState([]);
  const [quotas, setQuotas] = useState({
    departure: {},
    return: {},
  });
  const [tripType, setTripType] = useState("round-trip");
  const [selectedOrderId, setSelectedOrderId] = useState(null); // Used to track dynamically selected previously placed order
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });
  const bookingRequestInFlight = useRef(false);
  const snapPopupInView = useRef(false);

  // Derived: which trip types has the user already purchased (paid or pending)?
  const existingTripTypes = React.useMemo(() => {
    const active = shuttleOrders.filter(o =>
      o.transaction_status === "settlement" ||
      o.transaction_status === "capture" ||
      o.transaction_status === "pending"
    );
    const types = new Set(active.map(o => o.tripType));
    // If user has round-trip, they have both
    if (types.has("round-trip")) {
      types.add("one-way-departure");
      types.add("one-way-return");
    }
    // If user has both one-way types, treat as complete
    return types;
  }, [shuttleOrders]);

  // Derived: can user still book more shuttle tickets?
  const canBookMore = React.useMemo(() => {
    const hasRoundTrip = existingTripTypes.has("round-trip");
    const hasDeparture = existingTripTypes.has("one-way-departure");
    const hasReturn = existingTripTypes.has("one-way-return");
    // Cannot book more if they have a round-trip or both one-way types
    if (hasRoundTrip) return false;
    if (hasDeparture && hasReturn) return false;
    return true;
  }, [existingTripTypes]);

  const SHUTTLE_COLLECTION = "pendaftaranShuttle2026";
  const SHUTTLE_PRICE_PP = 300000;
  const SHUTTLE_PRICE_ONE_WAY = 200000;
  const SHUTTLE_QUOTA = 28;
  const EVENT_ID = "JOTR2026";

  const pickupPoints = [
    {
      name: "GBK/Parkir Timur/Sentosa",
      meetingPoint: "GBK / Parkir Timur / Sentosa",
    },
  ];

  const returnToPoints = [
    {
      name: "GBK/Parkir Timur",
      meetingPoint: "GBK / Parkir Timur",
    },
  ];

  const destinationCibodas = "Cibodas";

  const departureSchedule = [
    { date: "Friday, July 24, 2026", times: ["10:00"] },
    { date: "Saturday, July 25, 2026", times: ["09:00", "13:00"] },
  ];

  const returnSchedule = [
    { date: "Sunday, July 26, 2026", times: ["15:00", "16:00"] },
  ];

  // Flattened for compatibility with existing logic if needed
  const departureTimes = departureSchedule.flatMap((s) =>
    s.times.map((t) => `${s.date} ${t} WIB`)
  );

  const returnTimes = returnSchedule.flatMap((s) =>
    s.times.map((t) => `${s.date} ${t} WIB`)
  );

  const shuttleDetails = {
    kendaraan: "Hi Ace / Big Bravo / Big Alpha",
    harga: tripType === "round-trip" ? SHUTTLE_PRICE_PP : SHUTTLE_PRICE_ONE_WAY,
  };

  // Inisialisasi Midtrans Snap
  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    return () => clearInterval(checkSnap);
  }, []);

  // Fetch kuota keberangkatan dan kepulangan
  const fetchQuotas = useCallback(async () => {
    try {
      const departureQuotaData = {};
      const returnQuotaData = {};

      const ACTIVE_STATUSES = ["settlement", "capture", "pending"];

      // Kuota keberangkatan
      for (const point of pickupPoints) {
        departureQuotaData[point.name] = {};
        for (const time of departureTimes) {
          const q = query(
            collection(db, SHUTTLE_COLLECTION),
            where("pickupPoint", "==", point.name),
            where("departureTime", "==", time),
            where("transaction_status", "in", ACTIVE_STATUSES)
          );
          const snapshot = await getDocs(q);
          departureQuotaData[point.name][time] = {
            booked: snapshot.size,
            available: SHUTTLE_QUOTA - snapshot.size,
            full: snapshot.size >= SHUTTLE_QUOTA,
          };
        }
      }

      // Kuota kepulangan
      for (const time of returnTimes) {
        const q = query(
          collection(db, SHUTTLE_COLLECTION),
          where("returnTime", "==", time),
          where("transaction_status", "in", ACTIVE_STATUSES)
        );
        const snapshot = await getDocs(q);
        returnQuotaData[time] = {
          booked: snapshot.size,
          available: SHUTTLE_QUOTA - snapshot.size,
          full: snapshot.size >= SHUTTLE_QUOTA,
        };
      }

      setQuotas({ departure: departureQuotaData, return: returnQuotaData });
    } catch (err) {
      console.error("[FETCH] Failed to fetch quotas:", err);
      showNotification("error", "Failed to load shuttle quota data.");
    }
  }, [showNotification, pickupPoints, departureTimes, returnTimes]);

  // Ambil data pengguna dan pemesanan shuttle
  const fetchUserDataAndOrders = useCallback(async () => {
    if (!user?.uid || fetchStatus.current.isFetching) return;

    fetchStatus.current.isFetching = true;
    try {
      setInitialLoading(true);
      setError("");

      let userDataResult = null;
      let fetchedOrders = [];

      // Fetch user data
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        userDataResult = {
          ...userDoc.data(),
          email: userDoc.data().email || user.email,
        };
      } else {
        userDataResult = { email: user.email };
      }

      const shuttleQuery = query(
        collection(db, SHUTTLE_COLLECTION),
        where("uid", "==", user.uid)
      );
      const shuttleSnapshot = await getDocs(shuttleQuery);
      shuttleSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.eventId === EVENT_ID || data.eventId === "JOTR") {
          if (["pending", "settlement", "capture"].includes(data.transaction_status)) {
            fetchedOrders.push({ id: doc.id, ...data });
          }
        }
      });
      fetchedOrders.sort((a,b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      if (isMounted.current) {
        setUserData(userDataResult);
        setShuttleOrders(fetchedOrders);
        // Auto-select the first pending payment order.
        // Plain checkout records are intentionally ignored here so abandoned/test
        // checkouts do not force the user into payment.
        const firstPending = fetchedOrders.find(o => o.transaction_status === "pending");
        if (firstPending) {
          setSelectedOrderId(firstPending.id);
        }
      }
    } catch (err) {
      console.error("[ERROR] fetchUserDataAndPendingOrder:", {
        error: err.message,
        code: err.code,
      });
      if (isMounted.current) {
        setError(
          err.code === "permission-denied"
            ? "Access denied. You do not have permission."
            : err.code === "unavailable"
            ? "Connection failed. Please check your network."
            : "Failed to load user data."
        );
        showNotification("error", "Failed to load user data.");
      }
    } finally {
      if (isMounted.current) {
        setInitialLoading(false);
        setLoading(false);
      }
      fetchStatus.current.isFetching = false;
    }
  }, [user, showNotification]);

  // Efek awal
  useEffect(() => {
    isMounted.current = true;
    if (user?.uid) {
      fetchUserDataAndOrders();
      fetchQuotas();
      if (pickupPoints.length === 1) setSelectedPickupPoint(pickupPoints[0].name);
      if (returnToPoints.length === 1) setSelectedReturnPoint(returnToPoints[0].name);
      // Auto-select first date if available
      if (departureSchedule.length > 0) setSelectedDepartureDate(departureSchedule[0].date);
    } else {
      setUserData(null);
      setShuttleOrders([]);
      setInitialLoading(false);
      setLoading(false);
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, fetchUserDataAndOrders, fetchQuotas]);

  // Auto-set tripType default berdasarkan tiket yang sudah dimiliki user
  useEffect(() => {
    if (existingTripTypes.has("one-way-departure") && !existingTripTypes.has("one-way-return")) {
      // User only has departure ticket, default to return-only for next purchase
      setTripType("one-way-return");
    } else if (existingTripTypes.has("one-way-return") && !existingTripTypes.has("one-way-departure")) {
      // User only has return ticket, default to departure-only for next purchase
      setTripType("one-way-departure");
    }
  }, [existingTripTypes]);

  // Handle simulasi pemesanan (untuk pengujian)
  const handleSimulateBooking = useCallback(async () => {
    if (initialLoading) {
      showNotification("warning", "Loading data, please wait.");
      return;
    }
    if (!user?.uid) {
      showNotification(
        "error",
        "Please login to simulate booking."
      );
      return;
    }
    const isDepartureFull = (date, time) => {
      const fullTime = `${date} ${time} WIB`;
      return quotas.departure[selectedPickupPoint]?.[fullTime]?.full;
    };

    const isReturnFull = (date, time) => {
      const fullTime = `${date} ${time} WIB`;
      return quotas.return[fullTime]?.full;
    };

    if (tripType !== "one-way-return" && (!selectedDepartureTime || !selectedPickupPoint)) {
      showNotification(
        "error",
        "Please select a departure schedule and location."
      );
      return;
    }
    if (tripType !== "one-way-departure" && (!selectedReturnTime || !selectedReturnPoint)) {
      showNotification(
        "error",
        "Please select a return schedule and location."
      );
      return;
    }
    if (
      !userData?.namaLengkap ||
      !userData?.telepon ||
      !userData?.alamat ||
      !userData?.gender ||
      !userData?.email
    ) {
      showNotification(
        "error",
        "Incomplete user data: Full Name, Phone, Address, Gender, and Email must be filled."
      );
      return;
    }

    setLoading(true);
    try {
      const orderId = `SIM-${Date.now()}`;

      const shuttleData = {
        uid: user.uid,
        tripType,
        pickupPoint: tripType === "one-way-return" ? "-" : selectedPickupPoint,
        returnPoint: tripType === "one-way-departure" ? "-" : selectedReturnPoint,
        departureTime: tripType === "one-way-return" ? "-" : selectedDepartureTime,
        returnTime: tripType === "one-way-departure" ? "-" : selectedReturnTime,
        harga: shuttleDetails.harga,
        eventId: EVENT_ID,
        status: "settlement",
        createdAt: new Date().toISOString(),
        email: userData.email,
        namaLengkap: userData.namaLengkap,
        telepon: userData.telepon,
        alamat: userData.alamat,
        gender: userData.gender,
      };

      // Simpan data ke Firebase
      await setDoc(doc(db, SHUTTLE_COLLECTION, orderId), shuttleData);

      showNotification(
        "success",
        "Booking simulation successful! Data saved to Firebase."
      );
      await fetchUserDataAndOrders();
      await fetchQuotas();
      navigate(
        `/myregistration?order_id=${orderId}&transaction_status=settlement`
      );
    } catch (err) {
      console.error(
        "[ERROR] handleSimulateBooking: Failed to simulate booking",
        {
          error: err.message,
        }
      );
      showNotification(
        "error",
        "Failed to simulate booking: " + err.message
      );
      setError("Failed to simulate booking: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    showNotification,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    navigate,
    fetchUserDataAndOrders,
    fetchQuotas,
    initialLoading,
  ]);

  const payWithSnap = useCallback((snapToken, callbacks = {}) => {
    if (!snapToken || !window.snap) {
      showNotification("error", "Invalid payment token.");
      return false;
    }
    if (snapPopupInView.current) {
      showNotification("warning", "Payment popup is already open. Please complete or close it first.");
      return false;
    }

    snapPopupInView.current = true;
    const releasePopup = () => {
      snapPopupInView.current = false;
    };

    try {
      window.snap.pay(snapToken, {
        onSuccess: async (...args) => {
          try {
            await callbacks.onSuccess?.(...args);
          } finally {
            releasePopup();
          }
        },
        onPending: async (...args) => {
          try {
            await callbacks.onPending?.(...args);
          } finally {
            releasePopup();
          }
        },
        onError: async (...args) => {
          try {
            await callbacks.onError?.(...args);
          } finally {
            releasePopup();
          }
        },
        onClose: async (...args) => {
          try {
            await callbacks.onClose?.(...args);
          } finally {
            releasePopup();
          }
        },
      });
      return true;
    } catch (err) {
      releasePopup();
      console.error("[PAYMENT] snap.pay failed:", err);
      showNotification(
        "error",
        err?.message?.includes("PopupInView")
          ? "Payment popup is already open. Please complete or close it first."
          : `Payment popup failed: ${err?.message || "Unknown error"}`
      );
      return false;
    }
  }, [showNotification]);

  // Handle pembayaran dengan snap token yang sudah ada
  const handleBayar = useCallback((snapToken) => {
    if (snapToken && window.snap) {
      payWithSnap(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Shuttle payment successful!");
          fetchUserDataAndOrders();
          fetchQuotas();
        },
        onPending: async () => {
          showNotification("warning", "Shuttle payment pending.");
          fetchUserDataAndOrders();
        },
        onError: () => {
          showNotification("error", "Shuttle payment failed.");
        },
        onClose: () => {
          console.log("[PAYMENT] Jendela pembayaran ditutup.");
        },
      });
    } else {
      showNotification("error", "Invalid payment token.");
    }
  }, [showNotification, fetchUserDataAndOrders, fetchQuotas, payWithSnap]);

  // Handle lanjutkan pembayaran untuk order yang sudah ada (expired/baru)
  // Meminta token baru dari server jika token lama expired
  const handleContinuePayment = useCallback(async (order) => {
    if (!isSnapReady) {
      showNotification("error", "Midtrans is being prepared, please wait.");
      return;
    }
    setLoading(true);
    try {
      const token = await getIdToken();
      const orderId = order.orderId || order.id;
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/shuttle-create-transaction`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { snapToken, status } = response.data;
      if (status === "settlement") {
        showNotification("success", "Shuttle order already paid!");
        fetchUserDataAndOrders();
        fetchQuotas();
      } else if (snapToken) {
        handleBayar(snapToken);
      } else {
        showNotification("error", "Failed to get payment token.");
      }
    } catch (err) {
      console.error("[ERROR] handleContinuePayment:", err);
      const msg = err.response?.data?.message || err.message;
      showNotification("error", `Failed to continue payment: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [isSnapReady, getIdToken, handleBayar, showNotification, fetchUserDataAndOrders, fetchQuotas]);

  // Handle konfirmasi pemesanan
  const handleConfirmBooking = useCallback(async () => {
    if (SHUTTLE_BOOKING_CLOSED) {
      showNotification("warning", "Shuttle booking is closed.");
      return;
    }
    if (bookingRequestInFlight.current) return;
    if (!isSnapReady) {
      showNotification("error", "Midtrans is being prepared, please wait.");
      return;
    }
    if (tripType !== "one-way-departure" && !selectedReturnPoint) return;

    // --- LAST-MILE QUOTA GUARD ---
    bookingRequestInFlight.current = true;
    setLoading(true);
    try {
      // Re-fetch quotas to be sure
      const ACTIVE_STATUSES = ["settlement", "capture", "pending"];
      
      // Check Departure if applicable
      if (tripType !== "one-way-return") {
        const q = query(
          collection(db, SHUTTLE_COLLECTION),
          where("pickupPoint", "==", selectedPickupPoint),
          where("departureTime", "==", selectedDepartureTime),
          where("transaction_status", "in", ACTIVE_STATUSES)
        );
        const snap = await getDocs(q);
        if (snap.size >= SHUTTLE_QUOTA) {
          showNotification("error", "Sorry, departure quota just filled up.");
          fetchQuotas();
          bookingRequestInFlight.current = false;
          setLoading(false);
          return;
        }
      }

      // Check Return if applicable
      if (tripType !== "one-way-departure") {
        const q = query(
          collection(db, SHUTTLE_COLLECTION),
          where("returnTime", "==", selectedReturnTime),
          where("transaction_status", "in", ACTIVE_STATUSES)
        );
        const snap = await getDocs(q);
        if (snap.size >= SHUTTLE_QUOTA) {
          showNotification("error", "Sorry, return quota just filled up.");
          fetchQuotas();
          bookingRequestInFlight.current = false;
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error("Quota guard error:", err);
    }
    // ----------------------------

    try {
      const token = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/shuttle-register`,
        {
          uid: user.uid,
          tripType,
          pickupPoint: tripType === "one-way-return" ? "-" : selectedPickupPoint,
          returnPoint: tripType === "one-way-departure" ? "-" : selectedReturnPoint,
          departureTime: tripType === "one-way-return" ? "-" : selectedDepartureTime,
          returnTime: tripType === "one-way-departure" ? "-" : selectedReturnTime,
          harga: shuttleDetails.harga,
          eventId: EVENT_ID,
          namaLengkap: userData?.namaLengkap || "",
          telepon: userData?.telepon || "",
          alamat: userData?.alamat || "",
          gender: userData?.gender || "",
          email: userData?.email || user.email || "",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { snapToken, orderId } = response.data;
      payWithSnap(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Shuttle booking successful!");
          try {
            await updateDoc(doc(db, SHUTTLE_COLLECTION, orderId), { transaction_status: "settlement" });
          } catch (e) {
            console.error(e);
          }
          await fetchUserDataAndOrders();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          showNotification("warning", "Shuttle payment not completed.");
          try {
            await updateDoc(doc(db, SHUTTLE_COLLECTION, orderId), { transaction_status: "pending" });
          } catch (e) {
            console.error(e);
          }
          await fetchUserDataAndOrders();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          showNotification("error", "Shuttle payment failed.");
        },
        onClose: () => {},
      });
    } catch (err) {
      console.error("[ERROR] handleConfirmBooking: Failed to book shuttle", {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMessage = err.response?.data?.errors
        ? `Failed to book shuttle: ${err.response.data.errors.join(", ")}`
        : `Failed to book shuttle: ${
            err.response?.data?.message || err.message
          }`;
      showNotification("error", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
      bookingRequestInFlight.current = false;
    }
  }, [
    user,
    isSnapReady,
    selectedPickupPoint,
    selectedReturnPoint,
    selectedDepartureTime,
    selectedReturnTime,
    userData,
    showNotification,
    navigate,
    fetchUserDataAndOrders,
    fetchQuotas,
    tripType,
    shuttleDetails.harga,
    getIdToken,
    payWithSnap
  ]);

  const handleButtonClick = useCallback(() => {
    if (SHUTTLE_BOOKING_CLOSED) {
      showNotification("warning", "Shuttle booking is closed.");
      return;
    }
    if (!user?.uid) {
      navigate("/login");
      showNotification("error", "Please login to book a bus.");
      return;
    }
    if (!isSnapReady) {
      showNotification("error", "Midtrans is being prepared, please wait.");
      return;
    }
    if (
      quotas.departure[selectedPickupPoint]?.[selectedDepartureTime]?.full
    ) {
      showNotification(
        "error",
        "Departure quota for this point and schedule is full."
      );
      return;
    }
    if (quotas.return[selectedReturnTime]?.full) {
      showNotification(
        "error",
        "Return quota for this schedule is full."
      );
      return;
    }
    if (tripType !== "one-way-return" && (!selectedDepartureTime || !selectedPickupPoint)) {
      showNotification(
        "error",
        "Please select a departure schedule and location."
      );
      return;
    }
    if (tripType !== "one-way-departure" && (!selectedReturnTime || !selectedReturnPoint)) {
      showNotification(
        "error",
        "Please select a return schedule and location."
      );
      return;
    }
    if (
      !userData?.namaLengkap ||
      !userData?.telepon ||
      !userData?.alamat ||
      !userData?.gender ||
      !userData?.email
    ) {
      showNotification(
        "error",
        "Incomplete user data: Full Name, Phone, Address, Gender, and Email must be filled."
      );
      return;
    }

    handleConfirmBooking();
  }, [
    user,
    navigate,
    showNotification,
    isSnapReady,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    initialLoading,
    handleConfirmBooking,
    tripType
  ]);

  return (
    <section className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Notifikasi Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="bg-red-50 text-red-700 p-4 rounded-2xl border border-red-200 mb-6 flex items-center justify-center shadow-none"
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-5 w-5 mr-3"
              />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col lg:flex-row gap-4 items-start mt-6">
          {/* Left Column (Orders + Form) */}
          <div className="w-full lg:flex-[2] flex flex-col gap-4">
            
            {/* Shuttle Orders List */}
            {shuttleOrders.length > 0 && (
              <div className="flex flex-col gap-4">
                {shuttleOrders.map((order) => {
                  const isPaid = order.transaction_status === "settlement" || order.transaction_status === "capture";
                  const isPending = order.transaction_status === "pending";
                  if (!isPaid && !isPending) return null;

                  const tripLabel = order.tripType === "round-trip" ? "Round Trip" : order.tripType === "one-way-departure" ? "Departure Only" : "Return Only";

                  return (
                    <motion.div 
                      key={order.id} 
                      onClick={() => setSelectedOrderId(order.id)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl border border-slate-200 p-4"
                    >
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Your Registration</h4>
                      {/* Header: Event Name + Status Badge */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-lg md:text-xl font-bold text-slate-800 uppercase">
                              Shuttle JOTR 2026 - {tripLabel}
                            </h3>
                          </div>
                          {/* Location */}
                          <div className="text-sm text-lime-600 mt-1 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            Cibodas, West Java - Indonesia
                          </div>
                        </div>
                      </div>

                      {/* Info Grid (2 Columns for Long Data) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Departure</div>
                          <div className="text-base font-semibold text-slate-800">
                            {order.tripType !== "one-way-return" ? (order.departureTime !== "-" ? order.departureTime : "—") : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Return</div>
                          <div className="text-base font-semibold text-slate-800">
                            {order.tripType !== "one-way-departure" ? (order.returnTime !== "-" ? order.returnTime : "—") : "—"}
                          </div>
                        </div>
                      </div>

                      {/* Info Grid - Standardized Alignment (12-col) */}
                      <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100">
                        <div className="col-span-2 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Order ID</div>
                          <div className="text-sm font-medium text-slate-700 font-mono md:whitespace-nowrap break-words">
                            {order.orderId || order.id}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Transaction Time</div>
                          <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap">
                            {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ", " + new Date(order.createdAt.seconds * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "-"}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Status</div>
                          <div className={`text-sm font-medium italic md:whitespace-nowrap ${
                            isPaid ? "text-lime-600" : "text-amber-500"
                          }`}>
                            {isPaid ? "Paid" : "Pending Payment"}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Formulir Pemesanan - Show if user can still book more tickets */}
            {canBookMore && (
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border border-slate-200 p-4"
            >
              {/* Integrated Search Selector */}
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Book Shuttle Ticket</h3>
                    <p className="text-sm text-gray-500">Select your travel schedule for JOTR 2026</p>
                  </div>
                  <div className="relative self-start">
                    <InputField
                      type="select"
                      value={tripType}
                      onChange={(e) => {
                        setTripType(e.target.value);
                        setSelectedDepartureDate("");
                        setSelectedDepartureTime("");
                        setSelectedReturnTime("");
                        setSelectedOrderId(null);
                      }}
                       options={[
                         { label: "Round Trip", value: "round-trip", disabled: existingTripTypes.has("one-way-departure") || existingTripTypes.has("one-way-return") },
                         { label: "Departure Only", value: "one-way-departure", disabled: existingTripTypes.has("one-way-departure") },
                         { label: "Return Only", value: "one-way-return", disabled: existingTripTypes.has("one-way-return") },
                       ]}
                      defaultOptionLabel="Select Type"
                      endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                      className="w-auto"
                      labelClassName="sr-only"
                    />
                  </div>
                </div>
              </div>
              {/* Smart Selection UI */}
              <div className="space-y-4 mt-4">
                {/* Bagian Keberangkatan */}
                {tripType !== "one-way-return" && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-lime-600 bg-white text-lime-600 flex items-center justify-center text-sm font-bold">1</div>
                      <h4 className="text-lg font-bold text-gray-800 uppercase tracking-tight">Select Departure Schedule</h4>
                    </div>

                    {/* Unified Info + Schedule Dropdown Container */}
                    <div className="border border-gray-200 rounded-2xl overflow-hidden">

                      {/* Route Info Row — balanced left/right */}
                      <div className="px-5 py-4 flex items-center gap-0 bg-gray-50">
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Departure</p>
                          <p className="text-sm font-bold text-gray-900">Jakarta</p>
                          <p className="text-xs text-gray-500 font-medium">{pickupPoints[0].meetingPoint}</p>
                        </div>

                        <div className="flex flex-col items-center gap-1 w-20 shrink-0">
                          <FontAwesomeIcon icon={faBus} size="sm" className="text-gray-400" />
                          <div className="w-14 h-[1px] bg-gray-300 relative text-gray-300">
                            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-1 bg-current rounded-full" />
                            <div className="absolute top-1/2 left-full -translate-y-1/2 -ml-1 w-1.5 h-1.5 border-t border-r border-current rotate-45" />
                          </div>
                          <p className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">3–4 Hours</p>
                        </div>

                        <div className="flex-1 text-right">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Destination</p>
                          <p className="text-sm font-bold text-gray-900">Cibodas</p>
                          <p className="text-xs text-gray-500 font-medium">Kawasan TNGGP</p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200" />

                      {/* Schedule Dropdown — 2 columns */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex gap-4">
                          {/* Left: Dropdown */}
                          <div className="flex-1">
                            <InputField
                              label="Departure Schedule"
                              labelClassName="text-[11px] font-semibold text-gray-400 mb-2"
                              type="select"
                              value={selectedDepartureTime}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSelectedDepartureTime(val);
                                const schedule = departureSchedule.find(s => s.times.some(t => `${s.date} ${t} WIB` === val));
                                setSelectedDepartureDate(schedule ? schedule.date : "");
                                setSelectedOrderId(null);
                              }}
                              options={departureSchedule.flatMap(s => 
                                s.times.map(t => {
                                  const timeStr = `${s.date} ${t} WIB`;
                                  const isFull = quotas.departure[pickupPoints[0].name]?.[timeStr]?.full;
                                  return {
                                    label: isFull ? `${timeStr} (FULL)` : timeStr,
                                    value: timeStr,
                                    disabled: isFull
                                  };
                                })
                              )}
                              defaultOptionLabel="Select Schedule"
                              endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            />
                          </div>

                          {/* Right: Info */}
                          <div className="w-14 md:w-36 shrink-0 border-l border-slate-200 px-1 md:pl-4">
                            <div className="h-4 mb-2" /> {/* Spacer for label alignment */}
                            <div className="h-[42px] flex items-center justify-center md:justify-start">
                              {selectedDepartureTime ? (() => {
                                const q = quotas.departure[pickupPoints[0].name]?.[selectedDepartureTime];
                                const booked = q?.booked ?? 0;
                                return (
                                  <p className="text-sm font-medium text-gray-800 tracking-tight text-center w-full">
                                    {booked} / {SHUTTLE_QUOTA} <span className="hidden md:inline ml-0.5">booked</span>
                                  </p>
                                );
                              })() : (
                                <p className="text-sm text-gray-300 font-normal">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                )}


                {/* Bagian Kepulangan */}
                {tripType !== "one-way-departure" && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-4 pt-4 border-t border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-lime-600 bg-white text-lime-600 flex items-center justify-center text-sm font-bold">2</div>
                      <h4 className="text-lg font-bold text-gray-800 uppercase tracking-tight">Select Return Schedule</h4>
                    </div>

                    {/* Unified Info + Schedule Dropdown Container */}
                    <div className="border border-gray-200 rounded-2xl overflow-hidden">

                      {/* Route Info Row — balanced left/right */}
                      <div className="px-5 py-4 flex items-center gap-0 bg-gray-50">
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Departure</p>
                          <p className="text-sm font-bold text-gray-900">Cibodas</p>
                          <p className="text-xs text-gray-500 font-medium">Kawasan TNGGP</p>
                        </div>

                        <div className="flex flex-col items-center gap-1 w-20 shrink-0">
                          <FontAwesomeIcon icon={faBus} size="sm" className="text-gray-400" />
                          <div className="w-14 h-[1px] bg-gray-300 relative text-gray-300">
                            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-1 bg-current rounded-full" />
                            <div className="absolute top-1/2 left-full -translate-y-1/2 -ml-1 w-1.5 h-1.5 border-t border-r border-current rotate-45" />
                          </div>
                          <p className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">3–4 Hours</p>
                        </div>

                        <div className="flex-1 text-right">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Destination</p>
                          <p className="text-sm font-bold text-gray-900">Jakarta</p>
                          <p className="text-xs text-gray-500 font-medium">GBK / Parkir Timur</p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200" />

                      {/* Schedule Dropdown — 2 columns */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex gap-4">
                          {/* Left: Dropdown */}
                          <div className="flex-1">
                            <InputField
                              label="Return Schedule"
                              labelClassName="text-[11px] font-semibold text-gray-400 mb-2"
                              type="select"
                              value={selectedReturnTime}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSelectedReturnTime(val);
                                const schedule = returnSchedule.find(s => s.times.some(t => `${s.date} ${t} WIB` === val));
                                setSelectedReturnDate(schedule ? schedule.date : "");
                                setSelectedOrderId(null);
                              }}
                              options={returnSchedule.flatMap(s => 
                                s.times.map(t => {
                                  const timeStr = `${s.date} ${t} WIB`;
                                  const isFull = quotas.return?.[timeStr]?.full;
                                  return {
                                    label: isFull ? `${timeStr} (FULL)` : timeStr,
                                    value: timeStr,
                                    disabled: isFull
                                  };
                                })
                              )}
                              defaultOptionLabel="Select Schedule"
                              endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            />
                          </div>

                          {/* Right: Info */}
                          <div className="w-14 md:w-36 shrink-0 border-l border-slate-200 px-1 md:pl-4">
                            <div className="h-4 mb-2" /> {/* Spacer for label alignment */}
                            <div className="h-[42px] flex items-center justify-center md:justify-start">
                              {selectedReturnTime ? (() => {
                                const q = quotas.return?.[selectedReturnTime];
                                const booked = q?.booked ?? 0;
                                return (
                                  <p className="text-sm font-medium text-gray-800 tracking-tight text-center w-full">
                                    {booked} / {SHUTTLE_QUOTA} <span className="hidden md:inline ml-0.5">booked</span>
                                  </p>
                                );
                              })() : (
                                <p className="text-sm text-gray-300 font-normal">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
            )}
          </div>

            {/* Sticky Sidebar / Information Area */}
            <div className="w-full lg:flex-1 lg:max-w-md space-y-4">
              <div className="sticky top-12 space-y-4">
                {(() => {
                  const activeOrder = shuttleOrders.find((o) => o.id === selectedOrderId);
                  const isPaidOrder = activeOrder ? (activeOrder.transaction_status === 'settlement' || activeOrder.transaction_status === 'capture') : false;
                
                  const displayTripType = activeOrder ? activeOrder.tripType : tripType;
                  const displayDeparture = activeOrder ? activeOrder.departureTime : selectedDepartureTime;
                  const displayReturn = activeOrder ? activeOrder.returnTime : selectedReturnTime;
                  const displayHarga = activeOrder ? activeOrder.harga : shuttleDetails.harga;

                  const hasSelection = activeOrder || (
                    (tripType === "round-trip" && selectedDepartureTime && selectedReturnTime) ||
                    (tripType === "one-way-departure" && selectedDepartureTime) ||
                    (tripType === "one-way-return" && selectedReturnTime)
                  );

                  return (
                    <>
                      {/* Dynamic Order Summary Card */}
                      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-none">
                        {hasSelection ? (
                          <>
                            {/* Trip Type Row */}
                            <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                              <p className="text-[11px] font-semibold text-gray-400">Trip Type</p>
                              <p className="text-sm font-medium text-gray-800 text-right">
                                {displayTripType === "round-trip" ? "Round Trip" : displayTripType === "one-way-departure" ? "Departure Only" : "Return Only"}
                              </p>
                            </div>
          
                            {/* Departure Row */}
                            {displayTripType !== "one-way-return" && (
                              <div className="flex items-start justify-between py-2.5 border-b border-gray-100 gap-4">
                                <p className="text-[11px] font-semibold text-gray-400 shrink-0">Departure</p>
                                <p className="text-sm font-medium text-gray-800 text-right">
                                  {displayDeparture ? displayDeparture : <span className="text-gray-300">—</span>}
                                </p>
                              </div>
                            )}
          
                            {/* Return Row */}
                            {displayTripType !== "one-way-departure" && (
                              <div className="flex items-start justify-between py-2.5 border-b border-gray-100 gap-4">
                                <p className="text-[11px] font-semibold text-gray-400 shrink-0">Return</p>
                                <p className="text-sm font-medium text-gray-800 text-right">
                                  {displayReturn ? displayReturn : <span className="text-gray-300">—</span>}
                                </p>
                              </div>
                            )}
          
                            {/* Price Row */}
                            <div className="flex items-center justify-between pt-3 mt-1">
                              <p className="text-[11px] font-semibold text-gray-400">Total</p>
                              <p className="text-lg font-bold text-gray-900">IDR {Number(displayHarga).toLocaleString('en-US')}<span className="text-xs font-normal text-gray-400 ml-1">/ pax</span></p>
                            </div>

                            {/* CTA Button inside Summary Card */}
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <Button
                                onClick={activeOrder ? () => {
                                  if(!isPaidOrder) handleContinuePayment(activeOrder);
                                } : handleButtonClick}
                                disabled={loading || (activeOrder && isPaidOrder) || (!activeOrder && SHUTTLE_BOOKING_CLOSED)}
                                className={`w-full text-sm font-bold rounded-xl shadow-none ${(activeOrder && isPaidOrder) || (!activeOrder && SHUTTLE_BOOKING_CLOSED) ? "bg-slate-300 text-slate-500 hover:bg-slate-300 pointer-events-none" : ""}`}
                              >
                                {loading ? "Processing..." : activeOrder ? (isPaidOrder ? "Paid" : "Continue Payment") : SHUTTLE_BOOKING_CLOSED ? "Booking Closed" : "Book Now"}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-300 py-2">No selection yet</p>
                        )}
                      </div>
      
                      {/* Ketentuan Card */}
                      <div className="bg-gray-50 rounded-2xl border border-slate-200 p-4 shadow-none">
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Terms & Conditions</h4>
                        <ul className="space-y-3">
                          {[
                            "Be present 30 mins before schedule",
                            "Prepare E-Ticket & Identity Card",
                            "Ticket Non-Refundable / No Reschedule",
                            "Max 1 medium suitcase + 1 cabin bag"
                          ].map((text, i) => (
                            <li key={i} className="flex items-start gap-3 text-[13px] text-gray-600">
                              <FontAwesomeIcon icon={faCheckCircle} size="xs" className="text-lime-600 mt-0.5 shrink-0" />
                              {text}
                            </li>
                          ))}
                        </ul>
                      </div>
      
                      {/* PIC Shuttle Card */}
                      <div className="bg-gray-50 rounded-2xl border border-slate-200 p-4 shadow-none">
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">PIC Shuttle</h4>
                        <div className="space-y-3">
                          {[
                            { name: "Pipit", phone: "08988257499" },
                          ].map(({ name, phone }) => (
                            <div key={name} className="flex items-center justify-between">
                              <span className="text-[13px] text-gray-600">{name}</span>
                              <a href={`tel:${phone}`} className="text-xs font-mono text-lime-600 font-bold">{phone}</a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })()}


              </div>
            </div>
          </div>



      </div>
    </section>
  );
};
